import React, { useState, useEffect } from 'react';
import { graphql, PageProps } from 'gatsby';
import { CareerDetailQuery } from 'types/graphql-types';

import styled from '@emotion/styled';
import SEO from 'util/seo';
import { CareerDetailTitle } from 'components/unique/careerDetail/careerDetailTitle';
import { CareerDetailScope } from 'components/unique/careerDetail/careerDetailScope';
import { CareerDetailIdealCandidate } from 'components/unique/careerDetail/careerDetailIdealCandidate';
import { CareerDetailSkills } from 'components/unique/careerDetail/careerDetailSkills';
import { CareerDetailTools } from 'components/unique/careerDetail/careerDetailTools';
import { CareerDetailApplicationOverview } from 'components/unique/careerDetail/careerDetailApplicationOverview';
import { CareerDetailCompanyOverview } from 'components/unique/careerDetail/careerDetailCompanyOverview';
import { CareerDetailApply } from 'components/unique/careerDetail/careerDetailApply';
import { CareerDetailParallaxBackground } from 'components/unique/careerDetail/careerDetailParallaxBackground';
import { headerHeightPc, headerHeightSp, mq } from 'style/variables';
import colors from 'style/colors';
import sizes from 'style/sizes';
import { TextButton } from 'components/atoms/textButton/';

type CareerDetailContextProps = {
  topics_id: number;
  slug: string;
};

const ParentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  z-index: 1;
`;

const ParallaxWrapper = styled.div`
  position: absolute;
  top: ${headerHeightPc}px;
  left: 0;
  width: 100%;
  height: 2000px;
  z-index: 0;

  ${mq.onlysp} {
    top: 64px;
  }
`;

const SectionLayout = styled.div`
  padding: calc(540px + ${headerHeightPc}px) 0 80px;
  position: relative;
  z-index: 4;

  ${mq.onlysp} {
    padding: calc(375px + 64px) 0 64px;
  }
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1160px;
  margin: auto;
  width: 100%;
  background-color: #fff;
  padding: 118px 60px 144px;
  overflow: hidden;

  > * + * {
    margin-top: 144px;
  }

  > *:nth-of-type(2) {
    margin-top: 125px;
  }

  ${mq.onlysp} {
    padding: 64px 32px 68px;

    > * + * {
      margin-top: 62px;
    }
    > *:nth-of-type(2) {
      margin-top: 59px;
    }
  }
`;

const SectionWrapper = styled.section``;

const TopicsBackToWrapTop = styled.div`
  display: flex;
  justify-content: center;
  
  ${mq.onlypc} {
    margin-bottom: 180px;
  }
  ${mq.onlysp} {
    margin-bottom: 120px;
  }
`;

const CareerDetail: React.FC<PageProps<CareerDetailQuery, CareerDetailContextProps>> = ({ data }) => {
  const item: any = data.allKurocoCareerList.nodes[0];
  
  // fv画像があればそれを使用、なければ空配列
  const fvImages = item?.fv || [];
  const fvSpImage = item?.fv_sp || null;
  
  // デフォルトのアスペクト比で初期化（16:9）
  const [imageAspectRatios, setImageAspectRatios] = useState<string[]>(
    fvImages.map(() => '16 / 9')
  );

  useEffect(() => {
    // fv画像の先読みとアスペクト比を非同期で取得して更新
    if (fvImages.length > 0) {
      fvImages.forEach((image: any, index: number) => {
        const img = new Image();
        const imageUrl = image.url_org || image.url;
        
        // 画像の先読み
        img.onload = () => {
          const aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
          setImageAspectRatios((prev) => {
            const newRatios = [...prev];
            newRatios[index] = aspectRatio;
            return newRatios;
          });
        };
        
        // 高優先度で画像を読み込む
        img.src = imageUrl;
      });
    }
  }, [fvImages]);

  if (!item) {
    return <p>No detail found.</p>;
  }

  return (
    <>
      <SEO title={`${item.subject}｜Career`} />
      <ParentWrapper>
        <ParallaxWrapper>
          <CareerDetailParallaxBackground 
            fvImages={fvImages}
            fvSpImage={fvSpImage}
            imageAspectRatios={imageAspectRatios}
          />
        </ParallaxWrapper>
        <CareerDetailTitle title={item.subject ?? ''} />
        <SectionLayout>
          <SectionContainer>
            {(item.scope_desc1 ||
              item.scope_desc2 ||
              item.scope_outputs_label ||
              (item.scope_outputs_image && item.scope_outputs_image.length > 0)) && (
              <SectionWrapper>
                <CareerDetailScope
                  desc1={item.scope_desc1 ?? ''}
                  desc2={item.scope_desc2 ?? ''}
                  outputsLabel={item.scope_outputs_label ?? ''}
                  outputsImage={
                    (item.scope_outputs_image ?? []).filter(Boolean) as Array<{
                      url: string;
                      desc: string;
                      url_org: string;
                    }>
                  }
                  scopeImage={item.scope_image || undefined}
                />
              </SectionWrapper>
            )}
            {item.ideal_candidate && item.ideal_candidate.length > 0 && (
              <SectionWrapper>
                <CareerDetailIdealCandidate
                  idealCandidate={
                    (item.ideal_candidate ?? []).filter(Boolean) as Array<{
                      url: string;
                      desc: string;
                      url_org: string;
                    }>
                  }
                />
              </SectionWrapper>
            )}
            {(item.skills_required || item.skills_preferred) && (
              <SectionWrapper>
                <CareerDetailSkills
                  skillsRequired={item.skills_required || undefined}
                  skillsPreferred={item.skills_preferred || undefined}
                />
              </SectionWrapper>
            )}
            {(item.tools_main || item.tools_sub) && (
              <SectionWrapper>
                <CareerDetailTools toolsMain={item.tools_main || undefined} toolsSub={item.tools_sub || undefined} />
              </SectionWrapper>
            )}
            {((item.application_overview_label && item.application_overview_label.length > 0) ||
              (item.application_overview_content && item.application_overview_content.length > 0)) && (
              <SectionWrapper>
                <CareerDetailApplicationOverview
                  labels={(item.application_overview_label || []).filter(Boolean) as string[]}
                  contents={(item.application_overview_content || []).filter(Boolean) as string[]}
                />
              </SectionWrapper>
            )}
            <SectionWrapper>
              <CareerDetailCompanyOverview />
            </SectionWrapper>
          </SectionContainer>
        </SectionLayout>
        <TopicsBackToWrapTop>
          <TextButton arrowDirection={'left'} to={'/career'}>
            Back to Index
          </TextButton>
        </TopicsBackToWrapTop>
        <CareerDetailApply applyUrl="https://herp.careers/v1/fourdigitinc/teOnL2WgvpqS" />
      </ParentWrapper>
    </>
  );
};

export default CareerDetail;

export const pageQuery = graphql`
  query CareerDetail($topics_id: Int!) {
    allKurocoCareerList(filter: { topics_id: { eq: $topics_id } }) {
      nodes {
        topics_id
        subject
        fv {
          url
          url_org
          desc
        }
        fv_sp {
          url
          url_org
          desc
        }
        scope_desc1
        scope_desc2
        scope_outputs_label
        scope_outputs_image {
          url
          url_org
          desc
        }
        ideal_candidate {
          url
          url_org
          desc
        }
        skills_required
        skills_preferred
        tools_main
        tools_sub
        application_overview_label
        application_overview_content
        scope_image {
          url
          url_org
          desc
        }
      }
    }
  }
`;
